import { useMemo } from "react";
import { atom } from "recoil";
import ROUTE_PATTERNS from "../consts/routes";

// store real time updates that are ready to be consumed by the app
export const realTimeUpdatesState = atom({
    key: 'real-time-updates',
    default: [],
})

export const clickedPathsState = atom({
    key: 'clicked-paths',
    default: [],
})

// map of paths to their query keys
export const pathQueryKeyMap = {
    [ROUTE_PATTERNS.DASHBOARD]: [
        'dashboardQueryKey',
    ],
    [ROUTE_PATTERNS.GAME_EDIT]: [
        'gameUri',
    ],
    [ROUTE_PATTERNS.TASKS_ORGANIZE]: [
        'challengesQueryKey',
    ],
    [ROUTE_PATTERNS.GAMES_PATH]: [
        'challengesQueryKey',
    ],
    [ROUTE_PATTERNS.TEAMS_ORGANIZE]: [
        'teamsQueryKey',
    ],
    [ROUTE_PATTERNS.GROUPS_ORGANIZE]: [
        'groupsQueryKey',
    ],
    [ROUTE_PATTERNS.GAMES_REWARDS]: [
        'rewardsQueryKey',
    ],
    [ROUTE_PATTERNS.GAMES_EXPORTS]: [
        'gameUri',
    ],
    [ROUTE_PATTERNS.ANSWERS_MONITOR]: [
        'infiniteAnswerCommentsQueryKey',
        'monitorQueryKey',
        'leaderboardQueryKey',
    ],
    [ROUTE_PATTERNS.GAMES_GREY]: [
        'gameUri',
        'gameBrandingUri',
    ],
    [ROUTE_PATTERNS.GAMES_REELS]: [
        'gameUri',
    ],
    [ROUTE_PATTERNS.TASKS_LIBRARY]: [
        'libraryQueryKey',
    ],
    [ROUTE_PATTERNS.GAMES_STREAM]: [
        'gameStreamQueryKey',
        'leaderboardQueryKey',
    ],
    [ROUTE_PATTERNS.TASKS_STREAM]: [
        'challengeStreamQueryKey',
        'leaderboardQueryKey',
    ],
    [ROUTE_PATTERNS.TEAMS_STREAM]: [
        'teamStreamQueryKey',
        'leaderboardQueryKey',
    ],
    [ROUTE_PATTERNS.TEAMS_LEADERBOARD]: [
        'leaderboardQueryKey',
    ],
    [ROUTE_PATTERNS.ANSWERS_SHOW]: [
        'answerQueryKey',
        'infiniteAnswerCommentsQueryKey',
    ],
    [ROUTE_PATTERNS.GAMES_SLIDESHOW]: [
        'gameStreamQueryKey',
    ],
    [ROUTE_PATTERNS.GAMES_PERSONALIZATIONS]: [
        'personalizationsUri',
    ],
    [ROUTE_PATTERNS.GAMES_ANALYTICS]: [
        'analyticsQueryKey',
    ],
    [ROUTE_PATTERNS.GAMES_LIVE]: [],
    [ROUTE_PATTERNS.GAMES_DONATE]: [
        'gameUri',
    ],
    [ROUTE_PATTERNS.GAMES_NPC]: [
        'npcsQueryKey',
    ],
    [ROUTE_PATTERNS.GAMES_DESIGNATIONS]: [
        'designationsQueryKey',
    ]
};

// Routes where real-time updates should be applied immediately rather than queued.
export const instantUpdateRoutes = [
    ROUTE_PATTERNS.GAME_EDIT,
];

// Message sources that should trigger instant refresh (bypass queue)
export const instantUpdateSources = [
    "ss.purchase.donation",
    "ss.purchase.library",
    "ss.dropbox.connected",
    "ss.game.reel",
    "ss.task.reel",
    "ss.team.reel",
    "ss.game.export",
];

/**
 * Single source of truth for route-to-listener mappings
 * Defines which event sources each route listens to and how to resolve IDs
 * 
 * @type {Object.<string, Object.<string, string|function>>}
 */
const routeListenerMap = {
    [ROUTE_PATTERNS.TEAMS_ORGANIZE]: {
        "ss.team.delete": "teamIds",
        "ss.team.update": "teamIds",
        "ss.team.create": "*",
    },
    [ROUTE_PATTERNS.GAMES_SLIDESHOW]: {
        "ss.answer.reject": "answerIds",
        "ss.answer.feature": (data) => data.featured ? "*" : null,
        "ss.answer.accept": "*",
        "ss.answer.create": "*",
    },
    [ROUTE_PATTERNS.TASKS_ORGANIZE]: {
        "ss.task.delete": "challengeIds",
        "ss.task.update": "challengeIds",
        "ss.task.create": "*",
    },
    [ROUTE_PATTERNS.DASHBOARD]: {
        "ss.game.update": "gameIds",
        "ss.game.delete": "gameIds",
        "ss.game.create": "*",
        "ss.game.clone": "*",
    },
    [ROUTE_PATTERNS.ANSWERS_MONITOR]: {
        "ss.answer.accept": "*",
        "ss.answer.feature": "answerIds",
        "ss.answer.like": "answerIds",
        "ss.answer.unlike": "answerIds",
        "ss.answer.reject": "answerIds",
        "ss.answer.update": "answerIds",
        "ss.answer.create": "*",
    },
    [ROUTE_PATTERNS.TEAMS_LEADERBOARD]: {
        "ss.game.score": "gameIds",
        "ss.game.rescore": "gameIds",
    },
    [ROUTE_PATTERNS.GAME_EDIT]: {
        "ss.game.update": "gameIds",
    },
    [ROUTE_PATTERNS.GAMES_STREAM]: {
        "ss.answer.accept": "*",
        "ss.answer.feature": "answerIds",
        "ss.answer.unfeature": "answerIds",
        "ss.answer.like": "answerIds",
        "ss.answer.unlike": "answerIds",
        "ss.answer.reject": "answerIds",
        "ss.answer.update": "answerIds",
    },
    [ROUTE_PATTERNS.TASKS_STREAM]: {
        "ss.answer.accept": "*",
        "ss.answer.feature": "answerIds",
        "ss.answer.unfeature": "answerIds",
        "ss.answer.like": "answerIds",
        "ss.answer.unlike": "answerIds",
        "ss.answer.reject": "answerIds",
        "ss.answer.update": "answerIds",
    },
    [ROUTE_PATTERNS.TEAMS_STREAM]: {
        "ss.answer.accept": "*",
        "ss.answer.feature": "answerIds",
        "ss.answer.unfeature": "answerIds",
        "ss.answer.like": "answerIds",
        "ss.answer.unlike": "answerIds",
        "ss.answer.reject": "answerIds",
        "ss.answer.update": "answerIds",
    },
};

/**
 * Check if a route has listeners for a specific event source
 * @param {string} route - Route pattern
 * @param {string} source - Event source (e.g., "ss.team.update")
 * @returns {boolean}
 */
export const hasListener = (route, source) => {
    return !!routeListenerMap[route]?.[source];
};

/**
 * Hook for managing page-specific websocket event listeners
 * @param {string} route - Route pattern (e.g., ROUTE_PATTERNS.TEAMS_ORGANIZE)
 * @param {object} data - Page data containing IDs or conditions
 * @returns {object} Listeners config for Refresher component
 * 
 * @example
 * const listeners = usePageListeners(ROUTE_PATTERNS.TEAMS_ORGANIZE, { teamIds });
 */
export const usePageListeners = (route, data = {}) => {
    return useMemo(() => {
        const config = routeListenerMap[route];
        if (!config) return {};
        
        const listeners = {};
        
        for (const [source, resolver] of Object.entries(config)) {
            if (typeof resolver === "function") {
                // Dynamic resolver (e.g., conditional logic), e.g. "ss.answer.feature": (data) => data.featured ? "*" : null
                const result = resolver(data);
                if (result) {
                    listeners[source] = result === "*" ? ["*"] : data[result] || [];
                }
            } else if (resolver === "*") {
                // Wildcard - always listen regardless of the data
                listeners[source] = ["*"];
            } else {
                // ID array resolver, e.g. "ss.team.update": "teamIds" -> data.teamIds must include the affected team
                listeners[source] = data[resolver] || [];
            }
        }
        
        return listeners;
    }, [route, data]);
};